import { useState } from 'react';
import { Upload, FileText, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

interface ExpenseAttachmentsProps {
  expenseId?: string;
  attachments: string[];
  onAttachmentsChange: (attachments: string[]) => void;
  readOnly?: boolean;
}

export function ExpenseAttachments({
  expenseId,
  attachments,
  onAttachmentsChange,
  readOnly = false,
}: ExpenseAttachmentsProps) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const newPaths: string[] = [];
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop();
        const path = `${expenseId || 'new'}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        
        const { error } = await supabase.storage
          .from('expense-attachments')
          .upload(path, file);
        
        if (error) throw error;
        newPaths.push(path);
      }
      onAttachmentsChange([...attachments, ...newPaths]);
      toast.success(`${newPaths.length} arquivo(s) enviado(s)`);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async (path: string) => {
    try {
      await supabase.storage.from('expense-attachments').remove([path]);
      onAttachmentsChange(attachments.filter((a) => a !== path));
      toast.success('Arquivo removido');
    } catch {
      toast.error('Erro ao remover arquivo');
    }
  };

  const getPublicUrl = (path: string) => {
    const { data } = supabase.storage.from('expense-attachments').getPublicUrl(path);
    return data.publicUrl;
  };

  const getFileName = (path: string) => {
    const parts = path.split('/');
    return parts[parts.length - 1];
  };

  return (
    <div className="space-y-2">
      <Label>Anexos / Comprovantes</Label>
      
      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((path) => (
            <div key={path} className="flex items-center gap-2 text-sm p-2 rounded-md bg-muted/50">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate flex-1">{getFileName(path)}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => window.open(getPublicUrl(path), '_blank')}
              >
                <ExternalLink className="h-3 w-3" />
              </Button>
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive"
                  onClick={() => handleRemove(path)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div>
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              className="hidden"
              onChange={handleUpload}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
              disabled={uploading}
            />
            <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
              <span>
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Enviando...' : 'Adicionar Arquivo'}
              </span>
            </Button>
          </label>
        </div>
      )}
    </div>
  );
}
